import type {
	SRT_ColumnDef,
	SRT_SortingState,
} from '@/shared/components/master-table'
import type { ExportModel } from '@/shared/generated'
import type { TFunction } from 'i18next'

import React, { useMemo, useState } from 'react'
import { DownloadIcon, Trash2Icon } from 'lucide-react'
import prettyBytes from 'pretty-bytes'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import {
	useDeleteExportMutation,
	useDownloadExportFileMutation,
	useExportsQuery,
} from '@/entities/data-export'
import { UsernameById } from '@/entities/user'

import { DatetimeDistanceField } from '@/shared/components/data-grid/fields'
import PlainTextField from '@/shared/components/data-grid/fields/PlainText/TableCell'
import {
	createSRTColumnHelper,
	MasterTable,
	useMasterReactTable,
} from '@/shared/components/master-table'
import NoData from '@/shared/components/placeholders/NoData'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { confirm } from '@/shared/components/ui/confirmer'
import {
	Modal,
	ModalBody,
	ModalContent,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { ExportStatus, ExportType } from '@/shared/generated'
import { getMasterTableLocale } from '@/shared/i18n'
import { ampli } from '@/shared/lib/ampli'

type ExportsModalProps = {
	isOpen: boolean
	close(): void
}
const downloadFromUrl = (url: string) => {
	const a = document.createElement('a')
	a.href = url
	a.setAttribute('download', '')
	document.body.appendChild(a)
	a.click()
	a.remove()
}
export function DataExportsModal({ isOpen, close }: ExportsModalProps) {
	const { t } = useTranslation()

	const exportsQuery = useExportsQuery()
	const deleteExportMutation = useDeleteExportMutation({
		onSuccess: () => {
			exportsQuery.refetch()
		},
	})
	const generateLink = useDownloadExportFileMutation({
		onSuccess: ({ url }) => {
			downloadFromUrl(url)
			toast.success(
				t('export.downloading.success', {
					defaultValue: 'Export is downloaded',
				})
			)
		},
		onError: () => {
			toast.error(
				t('export.downloading.failed', {
					defaultValue: 'Export downloading failed, please try again.',
				})
			)
		},
	})
	const handleDownload = (exportItem: ExportModel) =>
		generateLink.mutate(exportItem.id)
	const handleDelete = async (exportItem: ExportModel) => {
		const answer = await confirm({
			title: t('general_detete_confirm_title'),
			description: t('general_detete_confirm_subtitle'),
			actionText: t('general_delete', {
				ns: 'keys',
			}),
			cancelText: t('general_cancel', {
				ns: 'keys',
			}),
			ActionProps: {
				variant: 'destructive',
			},
		})
		if (answer) {
			ampli.exportsDeleteExport()
			toast.promise(deleteExportMutation.mutateAsync(exportItem.id), {
				loading: t('export.deleting.loading', { defaultValue: 'Deleting...' }),
				success: t('export.deleting.success', {
					defaultValue: 'Export deleted',
				}),
				error: t('export.deleting.failed', {
					defaultValue: 'Export deleting failed, please try again.',
				}),
			})
		}
	}
	const columns = useMemo(
		() =>
			getColumns(
				{
					onDelete: handleDelete,
					onDownload: handleDownload,
				},
				t
			),
		[handleDelete, handleDownload]
	)

	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<ModalContent className='max-w-5xl' data-testid='exports-list-modal-form'>
				<ModalHeader>
					<ModalTitle>
						{t('exports.modal_title', { defaultValue: 'Export service' })}
					</ModalTitle>
				</ModalHeader>
				<ModalBody>
					{!exportsQuery.isLoading ? (
						exportsQuery.data?.length ? (
							<ExportsTable rows={exportsQuery.data} columns={columns} />
						) : (
							<NoData
								imgClassName='w-72'
								className='flex-col py-10'
								title={t('exports.table.empty.title', {
									defaultValue: 'Looks like there are no "Exports" yet',
								})}
								subtitle={t('exports.table.empty.subtitle', {
									defaultValue: 'You will see all your exports here',
								})}
							/>
						)
					) : (
						<SkeletonLoader />
					)}
				</ModalBody>
			</ModalContent>
		</Modal>
	)
}

function ExportsTable({
	rows,
	columns,
}: {
	rows: ExportModel[]
	columns: SRT_ColumnDef<ExportModel, any>[]
}) {
	const [sorting, setSorting] = useState<SRT_SortingState>([
		{
			desc: true,
			id: 'created_at',
		},
	])

	const table = useMasterReactTable<ExportModel>({
		localization: getMasterTableLocale(),
		data: rows,
		columns,
		state: {
			sorting,
		},
		divideX: false,
		enableColumnActions: false,
		layoutMode: 'grid',
		defaultColumn: {
			minSize: 55,
			size: Number.MAX_SAFE_INTEGER,
			maxSize: Number.MAX_SAFE_INTEGER,
		},
		onSortingChange: setSorting,
	})

	return (
		<div className='border-base flex h-96 flex-1 flex-col overflow-hidden rounded-lg border border-solid text-sm'>
			<MasterTable table={table} />
		</div>
	)
}
const columnHelper = createSRTColumnHelper<ExportModel>()
const getColumns = (
	{
		onDelete,
		onDownload,
	}: {
		onDelete(item: ExportModel): void
		onDownload(item: ExportModel): void
	},
	t: TFunction
) => [
	columnHelper.accessor('name', {
		header: t('app.name', { defaultValue: 'Name' }),
		Cell: ({ cell }) => (
			<Tooltip>
				<TooltipTrigger asChild>
					<div className='cell-data-text truncate'>{cell.getValue()}</div>
				</TooltipTrigger>
				<TooltipContent>{cell.getValue()}</TooltipContent>
			</Tooltip>
		),
	}),
	columnHelper.accessor('type', {
		size: 88,
		header: t('app.type', { defaultValue: 'Type' }),
		Cell: ({ cell }) => {
			const { t } = useTranslation()

			const value = cell.getValue()

			return (
				<PlainTextField
					value={t(`export_type.${value}`, { defaultValue: value })}
				/>
			)
		},
	}),
	columnHelper.accessor('status', {
		size: 100,
		header: t('app.status', { defaultValue: 'Status' }),
		Cell: ({ cell }) => {
			const { t } = useTranslation()

			return getExportStatusBadge(cell.getValue(), t)
		},
	}),
	columnHelper.accessor('file_size', {
		size: 78,
		header: t('app.size', { defaultValue: 'Size' }),
		Cell: ({ cell }) => {
			const value = cell.getValue()

			return <PlainTextField value={value ? prettyBytes(value) : '-'} />
		},
	}),
	columnHelper.accessor('created_at', {
		size: 100,
		header: t('app.start_date', { defaultValue: 'Start date' }),
		Cell: ({ cell }) => <DatetimeDistanceField value={cell.getValue() + 'Z'} />,
	}),
	columnHelper.accessor('finished_at', {
		size: 100,
		header: t('app.finish_date', { defaultValue: 'Finish date' }),
		Cell: ({ cell }) => {
			const value = cell.getValue()

			return value ? (
				<DatetimeDistanceField value={cell.getValue() + 'Z'} />
			) : (
				'-'
			)
		},
	}),
	columnHelper.accessor('created_by', {
		size: 200,
		header: t('app.created_by', { defaultValue: 'Created by' }),
		Cell: ({ cell }) => (
			<div className='flex items-center justify-between'>
				<UsernameById userId={cell.getValue()} />
				<div className='flex items-center space-x-1'>
					{cell.row.original.type !== ExportType.Bigquery &&
						cell.row.original.status === ExportStatus.Finished && (
							<Button
								data-testid='export-download-button'
								size='icon-sm'
								variant='ghost'
								onClick={() => onDownload(cell.row.original)}
							>
								<DownloadIcon />
							</Button>
						)}
					<Button
						data-testid='export-delete-button'
						size='icon-sm'
						variant='ghost'
						onClick={() => onDelete(cell.row.original)}
					>
						<Trash2Icon />
					</Button>
				</div>
			</div>
		),
	}),
]
function getExportStatusBadge(status: ExportStatus, t: TFunction) {
	switch (status) {
		case ExportStatus.Finished:
			return (
				<Badge size='sm' variant='success'>
					{t('app.ready', { defaultValue: 'Ready' })}
				</Badge>
			)
		case ExportStatus.Started:
			return (
				<Badge size='sm' variant='warning'>
					{t('app.in_progress', { defaultValue: 'In progress' })}
				</Badge>
			)
		case ExportStatus.Failed:
			return (
				<Badge size='sm' variant='destructive'>
					{t('app.failed', { defaultValue: 'Failed' })}
				</Badge>
			)
	}
}
function SkeletonLoader() {
	return (
		<div className='flex h-96 flex-1 flex-col overflow-hidden rounded-lg'>
			<div className='flex flex-col space-y-4'>
				<div className='flex items-center'>
					<div className='w-4/12'>
						<Skeleton className='h-5 w-44 rounded-lg' />
					</div>
					<div className='w-2/12'>
						<Skeleton className='h-5 w-16 rounded-lg' />
					</div>
					<div className='w-2/12'>
						<Skeleton className='h-5 w-24 rounded-lg' />
					</div>
					<div className='w-4/12'>
						<Skeleton className='ml-auto h-5 w-32 rounded-lg' />
					</div>
				</div>
				<div className='flex items-center opacity-85'>
					<div className='w-4/12'>
						<Skeleton className='h-5 w-44 rounded-lg' />
					</div>
					<div className='w-2/12'>
						<Skeleton className='h-5 w-16 rounded-lg' />
					</div>
					<div className='w-2/12'>
						<Skeleton className='h-5 w-24 rounded-lg' />
					</div>
					<div className='w-4/12'>
						<Skeleton className='ml-auto h-5 w-32 rounded-lg' />
					</div>
				</div>
				<div className='flex items-center opacity-60'>
					<div className='w-4/12'>
						<Skeleton className='h-5 w-44 rounded-lg' />
					</div>
					<div className='w-2/12'>
						<Skeleton className='h-5 w-16 rounded-lg' />
					</div>
					<div className='w-2/12'>
						<Skeleton className='h-5 w-24 rounded-lg' />
					</div>
					<div className='w-4/12'>
						<Skeleton className='ml-auto h-5 w-32 rounded-lg' />
					</div>
				</div>
				<div className='flex items-center opacity-40'>
					<div className='w-4/12'>
						<Skeleton className='h-5 w-44 rounded-lg' />
					</div>
					<div className='w-2/12'>
						<Skeleton className='h-5 w-16 rounded-lg' />
					</div>
					<div className='w-2/12'>
						<Skeleton className='h-5 w-24 rounded-lg' />
					</div>
					<div className='w-4/12'>
						<Skeleton className='ml-auto h-5 w-32 rounded-lg' />
					</div>
				</div>
			</div>
		</div>
	)
}
